import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  RmBodyZone,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "randmarcomps";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link as RouterLink, useParams } from "react-router-dom";
import GeneralDocumentSummary from "../components/document/GeneralDocumentSummary";
import CurrencyUtils from "../components/util/CurrencyUtils";
import RmPageHeader from "../components/layout/RmPageHeader";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import WithManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";
import InlineSpinner from "../components/util/InlineSpinner";

const addDefaultSrc = (ev: React.SyntheticEvent<HTMLImageElement>) => {
  ev.currentTarget.src = "https://api.randmar.io/Images/Logo.svg";
};

const Logo: React.FC<{
  reseller?: { name?: string | null; resellerId?: string | null };
}> = ({ reseller }) => {
  return (
    <img
      className="h-[110px] w-full rounded-xl border border-border bg-muted p-2 object-contain transition-transform duration-200 hover:-translate-y-0.5"
      alt={reseller?.name ? `${reseller.name} logo` : "Reseller logo"}
      loading="lazy"
      decoding="async"
      src={`https://api.randmar.io/V4/Partner/${reseller?.resellerId}/Account/Logo`}
      onError={addDefaultSrc}
    />
  );
};

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const GeneralDocument: React.FC<IProps> = ({ store, applicationId }) => {
  const [loading, setLoading] = React.useState(true);
  const { orderNumber } = useParams<{ orderNumber: string }>();

  const orderDetailsHeadingId = React.useId();
  const tableHeadingId = React.useId();
  const resellerHeadingId = React.useId();

  React.useEffect(() => {
    const fetchDocument = async () => {
      const { documentsStore } = store!;
      documentsStore.generalDocument = undefined;
      setLoading(true);

      const docOrderNumber = (orderNumber || "").toUpperCase();
      await documentsStore.getGeneralDocument(applicationId, docOrderNumber);

      setLoading(false);
    }
    fetchDocument();
  }, [applicationId, orderNumber, store]);

  const renderLoading = () => (
    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status" aria-live="polite">
      <InlineSpinner size="sm" decorative />
      Fetching the latest order details…
    </div>
  );

  const renderError = () => (
    <Alert variant="destructive" role="alert" className="rounded-2xl">
      <AlertTitle>Couldn’t load document</AlertTitle>
      <AlertDescription>Please try again or refresh the page.</AlertDescription>
    </Alert>
  );

  const { documentsStore, warehousesStore } = store!;
  const { generalDocument } = documentsStore;
  type GeneralDocumentOrderDetail = NonNullable<NonNullable<typeof generalDocument>["activeOrderDetails"]>[number];
  const renderOrderRows = (orderDetails?: GeneralDocumentOrderDetail[]) =>
    (orderDetails || [])
      .slice()
      .sort((a, b) => (a.mpn || "").localeCompare(b.mpn || ""))
      .map((item) => (
        <TableRow
          key={`${item.randmarSKU}-${item.warehouseCode}-${item.quantity}`}
          className="hover:bg-muted/50"
          aria-label={`Part ${item.mpn || item.randmarSKU}, quantity ${item.quantity}`}
        >
          <TableCell className="font-medium">
            <RouterLink
              to={Path.product(applicationId, item.randmarSKU)}
              className="inline-flex flex-col gap-0.5 text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2"
            >
              <span className="text-foreground">{item.mpn}</span>
              <span className="text-xs text-muted-foreground">SKU {item.randmarSKU}</span>
            </RouterLink>
          </TableCell>
          <TableCell className="text-center text-sm">
            {item.quantity}
          </TableCell>
          <TableCell className="text-center text-sm">
            <div className="space-y-0.5">
              <div>{item.unitWeight || 0} lbs</div>
              {item.quantity !== 1 ? (
                <div className="font-semibold text-foreground">{item.extendedWeight || 0} lbs</div>
              ) : null}
            </div>
          </TableCell>
          <TableCell className="text-center text-sm">
            <div className="space-y-0.5">
              <div><FormattedNumber value={item.unitPrice || 0} {...CurrencyUtils.format} /></div>
              {item.quantity !== 1 ? (
                <div className="font-semibold text-foreground">
                  <FormattedNumber value={item.extendedPrice || 0} {...CurrencyUtils.format} />
                </div>
              ) : null}
            </div>
          </TableCell>
          <TableCell className="text-center text-sm">
            {warehousesStore.getWarehouseNameFromCode(item.warehouseCode) || "TBD"}
          </TableCell>
        </TableRow>
      ));

  const orderDateLabel = (() => {
    if (!generalDocument?.orderDate)
      return undefined;

    const parsed = new Date(generalDocument.orderDate);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toLocaleDateString();
  })();

  const pageActions = generalDocument ? (
    <div className="flex flex-wrap items-center gap-2">
      {orderDateLabel ? (
        <Badge variant="secondary" size="sm">
          Ordered {orderDateLabel}
        </Badge>
      ) : null}
      {generalDocument.allowPartialShipment !== undefined ? (
        <Badge variant={generalDocument.allowPartialShipment ? "success" : "secondary"} size="sm">
          {generalDocument.allowPartialShipment ? "Partial shipment allowed" : "Full shipment"}
        </Badge>
      ) : null}
    </div>
  ) : undefined;

  const content = (() => {
    if (generalDocument === null) {
      return renderError();
    }

    if (loading || !generalDocument) {
      return renderLoading();
    }

    const businessAddress = (() => {
      const addressParts = [
        generalDocument.reseller?.address1,
        generalDocument.reseller?.address2,
        [generalDocument.reseller?.city, generalDocument.reseller?.province].filter(Boolean).join(", "),
        generalDocument.reseller?.postalCode,
      ].filter(Boolean);

      return addressParts.length > 0 ? addressParts.join(", ") : "No address on file";
    })();

    const activeOrderDetails = generalDocument.activeOrderDetails || [];
    const completedOrderDetails = generalDocument.completedSalesOrderDetails || [];
    const hasOrderDetails = activeOrderDetails.length > 0 || completedOrderDetails.length > 0;
    const canViewProfile = Boolean(generalDocument.reseller?.resellerId);

    return (
      <main className="space-y-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <section className="w-full flex-1 space-y-6">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger aria-controls="general-document-summary" id={orderDetailsHeadingId}>
                  Order details
                </AccordionTrigger>
                <AccordionContent id="general-document-summary" role="region" aria-labelledby={orderDetailsHeadingId}>
                  <GeneralDocumentSummary generalDocument={generalDocument} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {activeOrderDetails.length > 0 && (
              <Card aria-label="Open orders summary" className="rounded-2xl border border-border shadow-sm">
                <OpenOrdersSummary openOrders={activeOrderDetails} />
              </Card>
            )}

            <section aria-labelledby={tableHeadingId} className="space-y-3">
              <h2 id={tableHeadingId} className="text-xl font-semibold text-foreground">
                Line items
              </h2>

              <Card className="overflow-hidden rounded-2xl border border-border shadow-sm">
                <CardContent className="p-0">
                  <Table aria-labelledby={tableHeadingId}>
                    <TableHeader>
                    <TableRow>
                      <TableHead>Part number</TableHead>
                      <TableHead className="text-center">Qty</TableHead>
                      <TableHead className="text-center">Weight</TableHead>
                      <TableHead className="text-center">Price</TableHead>
                      <TableHead className="text-center">Location</TableHead>
                    </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hasOrderDetails ? (
                        <>
                          {renderOrderRows(activeOrderDetails)}
                          {renderOrderRows(completedOrderDetails)}
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                            There are no order lines yet. Check back once the order is confirmed.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </section>
          </section>

          <Card className="w-full lg:max-w-sm rounded-2xl border border-border shadow-sm" aria-labelledby={resellerHeadingId}>
            <CardHeader>
              <CardTitle id={resellerHeadingId}>{generalDocument.reseller?.name || "Reseller"}</CardTitle>
              <CardDescription>Reseller overview</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-border bg-muted p-2">
                <Logo reseller={generalDocument.reseller} />
              </div>

              {generalDocument.reseller?.priority ? (
                <Badge variant="info" size="sm" className="w-fit">
                  Priority: {generalDocument.reseller.priority}
                </Badge>
              ) : null}

              <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-4">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Main representative</dt>
                    <dd className="text-sm font-semibold text-foreground">{generalDocument.reseller?.contactName || "Not listed"}</dd>
                  </div>
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business address</dt>
                    <dd className="text-sm font-semibold text-foreground">{businessAddress}</dd>
                  </div>
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</dt>
                    <dd className="text-sm font-semibold text-foreground">{generalDocument.reseller?.publicEmail || "Not shared"}</dd>
                  </div>
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone</dt>
                    <dd className="text-sm font-semibold text-foreground">{generalDocument.reseller?.phone || "Not shared"}</dd>
                  </div>
                </dl>
              </div>

              <Separator />

              {canViewProfile ? (
                <Button
                  asChild
                  className="w-full rounded-xl"
                >
                  <RouterLink to={Path.reseller(applicationId, generalDocument.reseller?.resellerId)} aria-label="View full reseller profile" className="block w-full">
                    View complete profile
                  </RouterLink>
                </Button>
              ) : (
                <Button className="w-full" disabled aria-disabled="true">
                  Profile unavailable
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    );
  })();

  return (
    <>
      <RmPageHeader
        title="General Document"
        subtitle={generalDocument?.orderNumber || (orderNumber || "").toUpperCase()}
        rightBottomActions={pageActions}
      />
      <RmBodyZone className="space-y-6 pb-10">
        {content}
      </RmBodyZone>
    </>
  );
}

export default withRoot(WithManufacturer(inject("store")(observer(GeneralDocument))));
